import IMAGE_MAP from '../data/configurator-slider-images';

// Largest width ever requested from Imgix; caps over-fetch on high-DPR screens.
export const CONFIGURATOR_IMAGE_MAX_WIDTH = 1600;

/**
 * Resolve a configurator slider image path from the class tokens the slider
 * builds for a slide (the same tokens getSliderAssetClasses() produces).
 *
 * The lookup mirrors CSS class matching: it tries the most specific token set
 * first (model.edition.colour.option) and drops trailing tokens until a known
 * key matches, so an unselected option falls back to the base image.
 *
 * NOTE: only trailing tokens are dropped, which assumes at most one option
 * token (the current data set has exactly one: `sunroof`). With two
 * simultaneous options it could match `…colour.optA` or the base, but never
 * `…colour.optB`; revisit this if a second exterior/interior option image is
 * ever added.
 *
 * @param {String} assetType 'exterior' or 'interior'.
 * @param {Array} classes Ordered class tokens; option groups may be space-joined.
 * @returns {String} Relative path under images/configurator/, or '' if none.
 */
export function resolveConfiguratorImagePath(assetType, classes) {
    const tokens = classes
        .join(' ')
        .split(/\s+/)
        .filter(Boolean);

    for (let end = tokens.length; end >= 1; end--) {
        const key = [assetType, ...tokens.slice(0, end)].join('.');
        if (IMAGE_MAP[key]) return IMAGE_MAP[key];
    }

    return '';
}

/**
 * Build an Imgix URL for a configurator image path, with on-the-fly format and
 * compression so clients receive WebP/AVIF sized to the viewport instead of the
 * multi-MB source PNG.
 *
 * @param {String} imgixBase Imgix domain base (e.g. https://mgmotor.imgix.net).
 * @param {String} path Relative path under images/configurator/.
 * @param {Number} [width] Target pixel width.
 * @returns {String} Full Imgix URL, or '' when base/path are missing.
 */
export function buildConfiguratorImageUrl(imgixBase, path, width) {
    // The `/\.[a-z]/i` test requires a real domain, rejecting the degenerate
    // 'https://' that results when IMGIX_DOMAIN is unset — so we emit '' (no
    // background) rather than a malformed URL.
    if (!path || !imgixBase || !/\.[a-z]/i.test(imgixBase)) return '';

    const base = imgixBase.replace(/\/+$/, '');
    const params = ['auto=format,compress', 'q=75', 'fit=max'];
    if (width) params.push(`w=${width}`);

    return `${base}/images/configurator/${path}?${params.join('&')}`;
}

/**
 * Compute the CSS background-image style object for a slider slide.
 *
 * @param {Object} args
 * @param {String} args.imgixBase Imgix domain base.
 * @param {String} args.assetType 'exterior' or 'interior'.
 * @param {Array} args.classes Ordered class tokens for the slide.
 * @param {Number} [args.width] Target pixel width.
 * @returns {Object} A `{ backgroundImage }` style object, or `{}` when no image.
 */
export function configuratorSlideStyle({ imgixBase, assetType, classes, width }) {
    const path = resolveConfiguratorImagePath(assetType, classes);
    const url = buildConfiguratorImageUrl(imgixBase, path, width);

    return url ? { backgroundImage: `url('${url}')` } : {};
}
